import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useT } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  Users, ChevronLeft, Loader2, Plus, Copy, Check, X, Trash2, LogOut, Send,
  Crown, Sparkles, Link2,
} from "lucide-react";

/* List + Create page at /v2/ufl/teams */
export default function UflTeams() {
  const { t } = useT();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ufl/teams/mine");
      setData(data);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (name.trim().length < 2) return;
    setCreating(true);
    try {
      const { data } = await api.post("/ufl/teams", { name: name.trim() });
      toast.success(`Team '${data.name}' created`);
      setName("");
      navigate(`/v2/ufl/teams/${data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const respondInvite = async (invId, accept) => {
    try {
      await api.post(`/ufl/team-invites/${invId}/respond?accept=${accept}`);
      toast.success(accept ? "Joined team!" : "Declined.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-[#020617]"><Loader2 className="w-6 h-6 animate-spin text-[#A5B4FC]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#020617] text-white pb-24" data-testid="v2-ufl-teams">
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#7C3AED] text-white text-[9px] font-extrabold uppercase tracking-[0.3em]">
          <Users className="w-3 h-3" /> {t("ufl.teams.title")}
        </span>
        <button type="button" onClick={() => navigate("/v2/ufl/mobile")} data-testid="v2-ufl-teams-back" className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white">
          <ChevronLeft className="w-3.5 h-3.5" /> {t("ufl.hub.back")}
        </button>
      </div>

      <div className="px-4 space-y-3">
        {/* Premium disclaimer */}
        <div className="rounded-2xl bg-[#FFD700]/10 border border-[#FFD700]/40 p-3 text-[11px] text-[#FFE99A] flex items-start gap-2" data-testid="v2-ufl-teams-disclaimer">
          <Sparkles className="w-4 h-4 flex-shrink-0 mt-0.5 text-[#FFD700]" />
          <span>{t("ufl.teams.disclaimer")}</span>
        </div>

        {/* Create new team */}
        <div className="rounded-2xl bg-[#0F172A] border border-white/10 p-3 space-y-2" data-testid="v2-ufl-teams-create">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC]">{t("ufl.teams.create")}</div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder={t("ufl.teams.namePlaceholder")}
              data-testid="v2-ufl-teams-create-name"
              style={{ fontSize: "16px" }}
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]"
            />
            <button
              type="button"
              onClick={create}
              disabled={creating || name.trim().length < 2}
              data-testid="v2-ufl-teams-create-btn"
              className="rounded-lg bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t("ufl.teams.createBtn")}
            </button>
          </div>
        </div>

        {/* Pending invites */}
        {data.pending_invites?.length > 0 && (
          <div className="space-y-2" data-testid="v2-ufl-teams-invites">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-300">{t("ufl.teams.invitesTitle")}</div>
            {data.pending_invites.map((inv) => (
              <div key={inv.id} data-testid={`v2-ufl-teams-invite-${inv.id}`} className="rounded-2xl bg-amber-500/10 border border-amber-500/40 p-3 flex items-center gap-3">
                <Users className="w-5 h-5 text-amber-300" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold truncate">{inv.team?.name}</div>
                  <div className="text-[10px] font-mono text-white/60">{t("ufl.teams.captain")}: @{inv.team?.captain?.username}</div>
                </div>
                <button onClick={() => respondInvite(inv.id, true)} data-testid={`v2-ufl-teams-inv-accept-${inv.id}`} className="rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">{t("ufl.teams.accept")}</button>
                <button onClick={() => respondInvite(inv.id, false)} data-testid={`v2-ufl-teams-inv-decline-${inv.id}`} className="rounded-full bg-white/10 text-white/70 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">{t("ufl.teams.decline")}</button>
              </div>
            ))}
          </div>
        )}

        {/* My teams */}
        <div className="space-y-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{t("ufl.teams.myTeams")}</div>
          {data.teams.length === 0 ? (
            <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-center text-sm text-white/60" data-testid="v2-ufl-teams-empty">
              {t("ufl.teams.empty")}
            </div>
          ) : data.teams.map((tm) => (
            <Link
              key={tm.id}
              to={`/v2/ufl/teams/${tm.id}`}
              data-testid={`v2-ufl-teams-row-${tm.id}`}
              className="block rounded-2xl bg-white/5 border border-white/10 hover:border-[#7C3AED]/60 p-3 flex items-center gap-3"
            >
              <Users className="w-6 h-6 text-[#A5B4FC]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold truncate">{tm.name}</div>
                <div className="text-[10px] font-mono text-white/60">
                  {tm.is_captain && <span className="text-[#FFD700]"><Crown className="inline w-3 h-3 mr-0.5" />{t("ufl.teams.captain")} · </span>}
                  {t("ufl.teams.membersCount", { n: tm.members.length, label: tm.members.length === 1 ? t("ufl.teams.memberSingular") : t("ufl.teams.memberPlural") })}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Detail page at /v2/ufl/teams/:id */
export function UflTeamDetail() {
  const { t } = useT();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [inviteName, setInviteName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/ufl/teams/${id}`);
      setTeam(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/v2/ufl/teams");
    }
  }, [id, navigate]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!inviteName.trim()) return;
    setBusy(true);
    try {
      await api.post(`/ufl/teams/${id}/invite`, { invitee_username: inviteName.trim() });
      toast.success(`Invite sent to @${inviteName.trim()}`);
      setInviteName("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const copyLink = async () => {
    if (!team?.share_token) return;
    const url = `${window.location.origin}/v2/ufl/join/${team.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("ufl.teams.copied"));
    } catch {
      // Fallback
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success(t("ufl.teams.copied"));
    }
  };
  const respondReq = async (reqId, accept) => {
    try {
      await api.post(`/ufl/team-requests/${reqId}/respond?accept=${accept}`);
      toast.success(accept ? "Member added" : "Request declined");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const leaveTeam = async () => {
    if (!confirm("Leave this team?")) return;
    try {
      await api.post(`/ufl/teams/${id}/leave`);
      toast(t("ufl.teams.leave"));
      navigate("/v2/ufl/teams");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteTeam = async () => {
    if (!confirm("Delete this team? This can't be undone.")) return;
    try {
      await api.delete(`/ufl/teams/${id}`);
      toast("Team deleted");
      navigate("/v2/ufl/teams");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!team) return <div className="min-h-screen flex items-center justify-center bg-[#020617]"><Loader2 className="w-6 h-6 animate-spin text-[#A5B4FC]" /></div>;

  return (
    <div className="min-h-screen bg-[#020617] text-white pb-24" data-testid={`v2-ufl-team-${team.id}`}>
      <div className="px-4 pt-5 pb-3 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#7C3AED] text-white text-[9px] font-extrabold uppercase tracking-[0.3em]">
          <Users className="w-3 h-3" /> {team.name}
        </span>
        <button type="button" onClick={() => navigate("/v2/ufl/teams")} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/70 hover:text-white">
          <ChevronLeft className="w-3.5 h-3.5" /> {t("ufl.hub.back")}
        </button>
      </div>

      <div className="px-4 space-y-3">
        <div className="rounded-2xl bg-gradient-to-br from-[#7C3AED]/15 to-[#0F172A] border border-[#7C3AED]/40 p-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#A5B4FC]">// {team.is_captain ? t("ufl.teams.captain") : t("ufl.teams.member")}</div>
          <div className="font-heading text-xl uppercase tracking-wider mt-1">{team.name}</div>
          <div className="text-[11px] text-white/60 mt-1">{t("ufl.teams.membersCount", { n: team.members.length, label: team.members.length === 1 ? t("ufl.teams.memberSingular") : t("ufl.teams.memberPlural") })}</div>
        </div>

        {/* Members */}
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
          <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-white/50 border-b border-white/10">Members</div>
          <div className="divide-y divide-white/5">
            {team.members.map((m) => (
              <div key={m.user_id} data-testid={`v2-ufl-team-member-${m.user_id}`} className="flex items-center gap-2 px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-[#1E3A8A]/40 flex items-center justify-center text-base">{m.avatar_emoji || "⚽"}</div>
                <div className="flex-1 text-xs font-bold truncate">@{m.username}</div>
                {m.user_id === team.captain_id && <Crown className="w-4 h-4 text-[#FFD700]" />}
              </div>
            ))}
          </div>
        </div>

        {/* Share link */}
        <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="v2-ufl-team-share">
          <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1"><Link2 className="w-3 h-3" /> Share invite link</div>
          <div className="text-[11px] text-white/60">{t("ufl.teams.shareHint")}</div>
          <button
            type="button"
            onClick={copyLink}
            data-testid="v2-ufl-team-copy-link"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-xs font-bold uppercase tracking-widest px-3 py-2"
          >
            <Copy className="w-3.5 h-3.5" /> {t("ufl.teams.copyLink")}
          </button>
        </div>

        {/* Captain-only invite + requests */}
        {team.is_captain && (
          <>
            <div className="rounded-2xl bg-white/5 border border-white/10 p-3 space-y-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">{t("ufl.teams.invite")}</div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  placeholder="@username"
                  data-testid="v2-ufl-team-invite-input"
                  style={{ fontSize: "16px" }}
                  className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#7C3AED]"
                />
                <button onClick={invite} disabled={busy || !inviteName.trim()} data-testid="v2-ufl-team-invite-send" className="rounded-lg bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t("ufl.teams.inviteSend")}
                </button>
              </div>
            </div>

            {(team.pending_invites?.length > 0 || team.pending_requests?.length > 0) && (
              <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
                {team.pending_invites?.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-amber-300 border-b border-white/10">{t("ufl.teams.pendingInvites")}</div>
                    <div className="divide-y divide-white/5">
                      {team.pending_invites.map((inv) => (
                        <div key={inv.id} className="flex items-center gap-2 px-3 py-2">
                          <div className="w-7 h-7 rounded-full bg-[#1E3A8A]/40 flex items-center justify-center text-sm">{inv.invitee?.avatar_emoji || "⚽"}</div>
                          <div className="flex-1 text-xs font-bold truncate">@{inv.invitee?.username}</div>
                          <span className="text-[9px] font-mono uppercase tracking-widest text-amber-300">pending</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {team.pending_requests?.length > 0 && (
                  <div>
                    <div className="px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-emerald-300 border-b border-t border-white/10">{t("ufl.teams.pendingRequests")}</div>
                    <div className="divide-y divide-white/5">
                      {team.pending_requests.map((req) => (
                        <div key={req.id} className="flex items-center gap-2 px-3 py-2">
                          <div className="w-7 h-7 rounded-full bg-[#1E3A8A]/40 flex items-center justify-center text-sm">{req.requester?.avatar_emoji || "⚽"}</div>
                          <div className="flex-1 text-xs font-bold truncate">@{req.requester?.username}</div>
                          <button onClick={() => respondReq(req.id, true)} data-testid={`v2-ufl-team-req-accept-${req.id}`} className="rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">{t("ufl.teams.accept")}</button>
                          <button onClick={() => respondReq(req.id, false)} data-testid={`v2-ufl-team-req-decline-${req.id}`} className="rounded-full bg-white/10 text-white/70 text-[10px] font-bold uppercase tracking-widest px-3 py-1">{t("ufl.teams.decline")}</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer actions */}
        <div className="flex items-center gap-2 pt-2">
          {!team.is_captain && team.is_member && (
            <button onClick={leaveTeam} data-testid="v2-ufl-team-leave" className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/20 text-white/80 text-[10px] font-mono uppercase tracking-widest px-3 py-1.5">
              <LogOut className="w-3 h-3" /> {t("ufl.teams.leave")}
            </button>
          )}
          {team.is_captain && (
            <button onClick={deleteTeam} data-testid="v2-ufl-team-delete" className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 border border-rose-500/40 text-rose-300 text-[10px] font-mono uppercase tracking-widest px-3 py-1.5">
              <Trash2 className="w-3 h-3" /> {t("ufl.teams.delete")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Join-by-link page at /v2/ufl/join/:token */
export function UflTeamJoinByLink() {
  const { token } = useParams();
  const { t } = useT();
  const navigate = useNavigate();
  const [team, setTeam] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/ufl/share/${token}`);
        if (!cancel) setTeam(data);
      } catch (e) { toast.error(formatApiError(e)); navigate("/v2/ufl/teams"); }
    })();
    return () => { cancel = true; };
  }, [token, navigate]);
  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ufl/share/${token}/join`);
      if (data.status === "already_member") {
        toast(t("ufl.teams.alreadyMember"));
      } else {
        toast.success(t("ufl.teams.requestSent"));
      }
      navigate(`/v2/ufl/teams/${data.team_id || team.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!team) return <div className="min-h-screen flex items-center justify-center bg-[#020617]"><Loader2 className="w-6 h-6 animate-spin text-[#A5B4FC]" /></div>;
  return (
    <div className="min-h-screen bg-[#020617] text-white px-4 py-12" data-testid="v2-ufl-team-join">
      <div className="max-w-md mx-auto rounded-3xl bg-gradient-to-br from-[#7C3AED]/15 to-[#0F172A] border border-[#7C3AED]/40 p-6 text-center space-y-3">
        <Users className="w-12 h-12 mx-auto text-[#A5B4FC]" />
        <div className="font-heading text-2xl uppercase tracking-wider">{team.name}</div>
        <div className="text-[12px] text-white/60">{t("ufl.teams.membersCount", { n: team.members.length, label: team.members.length === 1 ? t("ufl.teams.memberSingular") : t("ufl.teams.memberPlural") })}</div>
        <button onClick={join} disabled={busy} data-testid="v2-ufl-team-join-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-extrabold px-5 py-3 text-sm uppercase tracking-wider disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          Send join request
        </button>
      </div>
    </div>
  );
}
